import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../globals/dictionary'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useTwinTranslation from '../../useTwinTranslation'
import { FormElement, FormRender } from '../../../../forms/FormRenderer/types'
import { CustomerPayload } from '../../../reducers/customers/customer'
import { CustomSelectAndCreateSectionCustomer } from '../../../../specificComponents/Customers/CustomSelectSectionCustomers'
import { twinFetchPostJSON } from '../../../globals/data'
import { useNavigate } from 'react-router'
import { BeforeSubmitHandler, OnSubmit } from '../../../../forms/TwinForm/types'
import { getBillingGroups, getConfigParam, getDictionaryCountries } from '../../../reducers/getters'
import { BTOBillingTypeSellKeysInversed } from '@teinor/erp/types/company/config/billingGroup/billingTypeSell/billingTypeSellOption'
import { ImageInputFile } from '../../../../forms/ImageInput/types'
import { getDictionaryStates } from '../../../globals/countries'
import { sortCountries } from '../../../globals/sortCountries'
import useCustomerTypesDict from '../../customer/useCustomerTypesDict'
import useCountryState from '../../useCountry'
import { useTypeIdNumber } from '../../useTypeIdNumber'
import { CustomSellSelectCampaign, CustomSellSelectProject } from '../../../../specificComponents/sells/components'

interface CreateSellProps {
    customers: CustomerPayload
    baseURL: string
    BillingGroupId?: number
}

export type CustomerSellInfoTy = {
    typeBilling: BTOBillingTypeSellKeysInversed
    values: TwinDictionary
    CustomerId?: number
    projects: TwinDictionary | null
    campaigns: TwinDictionary | null
}

const btoBillingTypeSell: TwinDictionary = {
    0: 'customer',
    1: 'autoInvoice',
    2: 'customerHeredate',
}

const useCreateSellLogic = ({ customers, baseURL, BillingGroupId }: CreateSellProps) => {
    const [specialData, setSpecialData] = useState<CustomerSellInfoTy>({ typeBilling: 'customerHeredate', values: {}, CustomerId: undefined, projects: null, campaigns: null })
    const { billingTypesSell, myBillingGroupId } = useMemo(() => {
        const billingGroups = getBillingGroups()
        let myBillingGroupId = BillingGroupId || billingGroups?.defaultBillingGroupId
        return {
            billingTypesSell: billingGroups?.dictionaryBillingGroup?.[myBillingGroupId || 0]?.billingTypesSellDict || {},
            myBillingGroupId
        }
    }, [BillingGroupId])
    const [billingGroupRel, setBillingGroupRel] = useState<number[] | null>(getDefaultBillingSellOptions(billingTypesSell))
    const [file, setFile] = useState<ImageInputFile[]>([])
    const { country, setCountry } = useCountryState({ defaultCountry: '' })
    const { typesIdNumber } = useTypeIdNumber()
    const { dictCustomerType } = useCustomerTypesDict()
    const memoDefaultCustomerType = useMemo(() => getConfigParam('quoteOrderDefaultCustomerType'), [])
    const addressFields: FormRender<any> = []
    const myCustomerInfo: FormElement<any>[] = []
    const { t } = useTwinTranslation()

    const itemsColumns = {
        'reference': { value: 'reference', name: t('reference', 'Referencia') },
        'code_bar': { value: 'code_bar', name: t('barCode', 'Código de barras') },
        'supplier_reference': { value: 'supplier_reference', name: t('supplierRef', 'Referencia proveedor') }
    }
    const navigate = useNavigate()

    const createSell: OnSubmit = useCallback(async (res) => {
        const create = await res.json()
        return navigate(baseURL + '/' +  create.id)
    }, [navigate, baseURL])

    const getCustomerDefaulltValues = useCallback(async (CustomerId: number) => {
        if (CustomerId !== 0) {
            const myBillingGroup = await twinFetchPostJSON('/api/app/customer/customerBillingGroup/getBillingGroupCustomerInstance', { BillingGroupId: myBillingGroupId, CustomerId })
            setBillingGroupRel(() => myBillingGroup?.BillingTypeSellOptions ? myBillingGroup?.BillingTypeSellOptions : null)
        } else {
            setBillingGroupRel(getDefaultBillingSellOptions(billingTypesSell))
        }
    }, [setBillingGroupRel, billingTypesSell, myBillingGroupId])

    const handleCustomerInfo = useCallback(async (value: number) => {
        await getCustomerDefaulltValues(value)
        setSpecialData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, CustomerId: value }
        })
    }, [setSpecialData, getCustomerDefaulltValues])

    const handleCustomerCampaignProject = useCallback(async (_id: string, fieldName: string, value: string) => {
        setSpecialData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, [fieldName]: parseInt(value) }
        })
    }, [setSpecialData])

    const getCustomerCampaigns = useCallback(async () => {
        return await twinFetchPostJSON('/api/app/customer/campaign/customer/getAllCampaignCustomers', {
            where:
            {
                CustomerId: specialData.CustomerId
            },
        })
    }, [specialData.CustomerId])

    const getCustomerProjects = useCallback(async () => {
        return await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjects', {
            where:
            {
                'CustomerProject.active': true,
                CustomerId: specialData.CustomerId
            },
        })
    }, [specialData.CustomerId])

    const myCustomerProjectsOnSubmit = useCallback(async () => {
        const result = await getCustomerProjects()
        if (result) {
            setSpecialData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                copy.projects = dictionaryComplexFromJsonArr(result)
                return { ...copy }
            })
        }
    }, [getCustomerProjects])

    const myCustomerCampaignOnSubmit = useCallback(async () => {
        const result = await getCustomerCampaigns()
        if (result) {
            setSpecialData((old) => {
                const campaigns: TwinDictionary[] = []
                for (const element of result) {
                    campaigns.push({ ...element?.Campaign, id: element?.id, value: element.id, text: element?.Campaign?.name })
                }
                const copy = JSON.parse(JSON.stringify(old))
                copy.campaigns = campaigns
                return { ...copy }
            })
        }
    }, [getCustomerCampaigns])

    const searchItems = useCallback(async () => {
        const searchedItems = await getCustomerProjects()
        const searchedCampaignsItems = await getCustomerCampaigns()
        if (searchedItems && searchedCampaignsItems) {
            setSpecialData((old) => {
                const campaigns: TwinDictionary[] = []
                for (const element of searchedCampaignsItems) {
                    campaigns.push({ ...element?.Campaign, id: element?.id, value: element.id, text: element?.Campaign?.name })
                }

                const copy = JSON.parse(JSON.stringify(old))
                copy.projects = dictionaryComplexFromJsonArr(searchedItems)
                copy.campaigns = campaigns
                return { ...copy }
            })
        } else {
            setSpecialData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return { ...copy, projects: null, campaigns: null }
            })
        }
    }, [setSpecialData, getCustomerCampaigns, getCustomerProjects])


    const parseBTOBeforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const result: number[] = []
        for (const key in billingTypesSell) {
            const billingType = billingTypesSell[key]
            const index = 'BTO_' + billingType.id
            const element = vals[index];
            result.push(element)
        }
        vals['BillingTypeSellOptions'] = result
        return true
    }, [billingTypesSell])
    
    const customerInfo: FormElement<any>[] = []
    customerInfo.push({
        name: 'CustomerId',
        component: 'Custom',
        render: ({ extraProps }) => {
            return <CustomSelectAndCreateSectionCustomer name='CustomerId' customers={extraProps.customers} label={t('customer', 'Cliente')} onChange={handleCustomerInfo} required={true} />
        },
        extraProps: { customers }
    })

    if (specialData.CustomerId !== undefined && specialData.CustomerId  !== 0) {
        customerInfo.push({
            name: 'CustomerProjectId',
            component: 'Custom',
            render: ({ extraProps }) => {
                return <CustomSellSelectProject getCompleteData={myCustomerProjectsOnSubmit} headerData={specialData} items={extraProps.projects} handleChange={handleCustomerCampaignProject} name='CustomerProjectId' orderByOpp={true} viewLabel={false} />
            },
            extraProps: { projects: specialData?.projects || {} }
        })

        customerInfo.push({
            name: 'CampaignCustomerId',
            component: 'Custom',
            render: ({ extraProps }) => {
                return <CustomSellSelectCampaign getCompleteData={myCustomerCampaignOnSubmit} headerData={specialData} label={t('campaign', 'Campaña')} items={extraProps.campaigns} handleChange={handleCustomerCampaignProject} name='CampaignCustomerId' viewLabel={false} />
            },
            extraProps: { campaigns: specialData.campaigns ? specialData.campaigns : [] }
        })
    }
    const checkChangeTypeBilling = useCallback((values: TwinDictionary): BTOBillingTypeSellKeysInversed => {
        let result: BTOBillingTypeSellKeysInversed = 'autoInvoice'
        for (const key in billingTypesSell) {
            const billingTypeSell = billingTypesSell[key]
            const value = billingTypeSell?.billingTypeSellOptionsDict?.[parseInt(values[billingTypeSell.id])]
            if (value) {
                const myTypeBilling = btoBillingTypeSell[value.typeBilling]
                if (myTypeBilling) {
                    result = myTypeBilling
                    if (myTypeBilling !== 'supplierHeredate') {
                        break
                    }
                }
            }
        }
        return result
    }, [billingTypesSell])

    const handleChangeTypeBilling = useCallback((id: number, value: number) => {
        setSpecialData((old) => {
            const vals = { ...old.values, [id]: value }
            return {
                ...old,
                typeBilling: checkChangeTypeBilling(vals),
                values: vals,
            }
        })
    }, [setSpecialData, checkChangeTypeBilling])

    const extraBillingTypesSell: FormElement<any>[] = []
    if (specialData.CustomerId !== undefined) {
        for (const key in billingTypesSell) {
            const type = billingTypesSell[parseInt(String(key))]
            let value = type.DefaultBillingTypeSellOptionId
            if (billingGroupRel) {
                for (const element of billingGroupRel) {
                    if (element in type.billingTypeSellOptionsDict) {
                        value = element
                    }
                }
            }
            extraBillingTypesSell.push({
                label: type.name,
                name: 'BTO_' + type.id,
                component: 'CustomSelectWithSearchBar',
                items: type.billingTypeSellOptionsDict || {},
                required: type.mandatory,
                value,
                onChange: (value: number) => handleChangeTypeBilling(type.id, value)
            })
        }
    }

    const fileInfo: FormElement<any>[] = [
        {
            name: 'orderFilePath',
            component: 'FilesInput',
            label: t('uploadExcelOrCSV', 'Subir excel o CSV'),
            acceptFileConditions: {
                'text': ['.csv', '.xlsx']
            },
            onChange: setFile,
        },
    ]
    if (file.length) {
        fileInfo.push(
            {
                name: 'delimiter',
                label: t('delimiter', 'Delimitado'),
                component: 'InputWithLabelMargin',
                defaultValue: ';',
                required: true
            },
            {
                name: 'rowsToSkip',
                label: t('rowsToSkip', 'Filas que saltar'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'searchByFile',
                label: t('searchBy', 'Buscar por'),
                component: 'CustomSelectWithSearchBar',
                items: itemsColumns,
                required: true
            },
            {
                name: 'posColumnSelected',
                label: t('posColumnSelected', 'Posición columna seleccionada'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posQuantityColumn',
                label: t('posQuantityColumn', 'Posición columna cantidad'),
                component: 'InputWithLabelMarginIntegerStateFull',
                required: true
            },
            {
                name: 'posNotesColumn',
                label: t('posNotesColumn', 'Posición columna notas'),
                component: 'InputWithLabelMarginIntegerStateFull',
            },
        )
    }
    const createCustomer = specialData.CustomerId === 0
    if (createCustomer) {
        myCustomerInfo.push(
            {
                name: 'name',
                label: t('nameCustomer', 'Nombre del Cliente'),
                component: 'InputWithLabelMargin',
                required: true
            },
            {
                name: 'customer_type',
                label: t('customerType', 'Tipo de cliente'),
                items: dictCustomerType,
                component: 'CustomSelect',
                required: true,
                value: memoDefaultCustomerType
            },
            {
                name: 'type_id_number',
                label: t('typeIdNumber', 'Tipo de documento identificación'),
                component: 'CustomSelect',
                items: typesIdNumber,
                value: 0,
            },
            {
                name: 'id_number',
                label: t('id_number', 'DNI'),
                component: 'InputWithLabelMargin',
                type: 'text',
                required: true,
            }
        )
        addressFields.push(
            {
                cols: 1,
                elements: [
                    {
                        name: 'createAddress',
                        component: 'InputHidden',
                        value: 'true'
                    },
                    {
                        name: 'nameAddress',
                        label: t('nameAddress', 'Nombre de la dirección'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'name_to',
                        label: t('recipientName', 'Nombre del receptor'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'surname_to',
                        label: t('recipientSurname', 'Apellido del receptor'),
                        component: 'InputWithLabelMargin',
                        type: 'text'
                    },
                ],
            },
            {
                cols: 1,
                title: t('address', 'Dirección'),
                elements: [
                    {
                        name: 'address',
                        label: t('address', 'Dirección'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'additional_address',
                        label: t('additional_address', 'Dirección adicional'),
                        component: 'InputWithLabelMargin',
                        type: 'text'
                    },
                    {
                        name: 'postal_code',
                        label: t('postalCode', 'Código Postal'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'city',
                        label: t('city', 'Ciudad'),
                        component: 'InputWithLabelMargin',
                        type: 'text',
                        required: true
                    },
                    {
                        name: 'country',
                        label: t('country', 'País'),
                        component: 'CustomSelectWithSearchBar',
                        items: getDictionaryCountries(),
                        sortFunction: sortCountries,
                        required: true,
                        value: '',
                        onChange: (value: string) => setCountry(value)
                    },
                    {
                        name: 'state',
                        label: t('state', 'Provincia'),
                        component: 'CustomSelectWithSearchBar',
                        sortItems: true,
                        items: getDictionaryStates(country),
                        required: true,
                        value: ''
                    },
                    {
                        name: 'createWorkingCenter',
                        component: 'CheckboxMargin',
                        label: t('createWorkingcenter', 'Crear centro de trabajo'),
                    }
                ]
            }
        )
    }
    useEffect(() => {
        if (specialData.CustomerId) {
            searchItems()
        }
    }, [searchItems, specialData.CustomerId])
    return { handleCustomerInfo, customerInfo, createSell, extraBillingTypesSell, handleChangeTypeBilling, parseBTOBeforeSubmit, fileInfo, addressFields, myCustomerInfo }
}

const getDefaultBillingSellOptions = (billingTypesSell: TwinDictionary) => {
    let result: number[] | null = null
    for (const key in billingTypesSell) {
        const billingTypeSell = billingTypesSell[key]
        if (billingTypeSell.DefaultBillingTypeSellOptionId) {
            if (!result) {
                result = []
            }
            result.push(billingTypeSell.DefaultBillingTypeSellOptionId)
        }
    }
    return result
}

export default useCreateSellLogic